import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  StackedCell,
  Tag,
  StatusPill,
  FigurePill,
  NumCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { getPharmacyMedicinesListApi } from "../../../services/pharmacyMedicines.services";
import MedicineFormModal from "./MedicineFormModal";
import { useOrgFacilities } from "../common/useOrgFacilities";
import { formatAmount, formatMoney } from "../common/money";


/*
 * How a medicine reads at a glance. Stock is the leading signal - a medicine
 * that is active but has nothing on the shelf is not "Active" to anyone
 * standing at the counter.
 */
/* How many medicines the band reads to work out the split. A catalogue past
   this is counted on what came back, which is said rather than guessed at. */
const COUNT_LIMIT = 500;

const STATES = {
  inactive: { label: "Inactive", color: "#98A2B3", tone: ["#F2F4F7", "#475467"] },
  out: { label: "Out of Stock", color: "#F97066", tone: ["#FEF3F2", "#B42318"] },
  low: { label: "Low Stock", color: "#EAAA5E", tone: ["#FFFAEB", "#B54708"] },
  active: { label: "Active", color: "#12B76A", tone: ["#ECFDF3", "#027A48"] },
};

/*
 * What ?status= should carry for the two states the server cannot yet filter:
 *   low_stock      0 < current_stock <= stockout_limit
 *   out_of_stock   current_stock <= 0
 */
const STOCK_STATUS_PARAM = { low: "low_stock", out: "out_of_stock" };

const inr0 = (value) =>
  formatMoney(Math.round(Number(value) || 0)).replace(".00", "");

/* Whole units read as whole numbers; a part-unit keeps its decimal. */
const formatQty = (value) => {
  const qty = Number(value) || 0;
  return Number.isInteger(qty) ? qty.toLocaleString("en-IN") : qty.toFixed(2);
};

const stateOf = (record) => {
  if (record?.is_active === false) return "inactive";
  const stock = Number(record?.current_stock) || 0;
  if (stock <= 0) return "out";
  if (stock <= (Number(record?.stockout_limit) || 0)) return "low";
  return "active";
};

const Medicines = () => {
  const navigate = useNavigate();
  const { canTransfer } = useOrgFacilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [state, setState] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    if (searchParams.get("action") === "add") {
      setEditData(null);
      setOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (search) queryParams.search = search;
    /*
     * Every chip filters on the server. Active and Inactive go through the
     * stored is_active flag; Low Stock and Out of Stock use ?status=, in the
     * same form the purchase lists use.
     *
     * ?status= is not implemented on this endpoint yet, so those two currently
     * come back unfiltered - which is left as it is, so the gap shows.
     */
    if (state === "active") queryParams.is_active = true;
    if (state === "inactive") queryParams.is_active = false;
    if (STOCK_STATUS_PARAM[state]) queryParams.status = STOCK_STATUS_PARAM[state];
    getPharmacyMedicinesListApi(new URLSearchParams(queryParams))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, state, refresh]);

  /*
   * The band counts every medicine by state, and values the shelf.
   *
   * Both need the whole catalogue, and the list is paginated - so the figures
   * are read off the page already in hand and only when that page holds all of
   * it. A partial count read as a total would be worse than no figure at all.
   *
   * It used to fetch the catalogue a second time to work this out. One screen,
   * one request: the response the table is drawn from is the same response
   * these are counted from.
   */
  /*
   * The band counts the whole catalogue, which one page of it cannot.
   *
   * The split is worked out here rather than by the server - active, low,
   * out, inactive come from each medicine's own stock and flag, and the
   * endpoint has no counts of its own to offer. So the catalogue is read once
   * for counting, unpaginated, and kept.
   *
   * It used to be counted off whichever page the table was showing, and only
   * when that page happened to hold the whole list - which on a catalogue of
   * 28 split into pages of 25 is never, so the band simply never appeared.
   * Read once on its own, it is there from the first paint and stays put
   * while the table underneath is filtered and paged.
   */
  const [band, setBand] = useState(null);
  /* The catalogue the counts were read from, kept because the two stock
     buckets are answered out of it - see below. */
  const [catalogue, setCatalogue] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getPharmacyMedicinesListApi(new URLSearchParams({ page: 1, limit: COUNT_LIMIT }))
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.results || [];
        setCatalogue(rows);
        if (!rows.length) {
          setBand(null);
          return;
        }

        const counts = { inactive: 0, out: 0, low: 0, active: 0 };
        let units = 0;
        let value = 0;
        rows.forEach((row) => {
          counts[stateOf(row)] += 1;
          /* Negative stock is a bookkeeping state, not shelf worth money. */
          const stock = Math.max(0, Number(row.current_stock) || 0);
          units += stock;
          value += stock * (Number(row.rate_a) || 0);
        });
        setBand({
          counts,
          units,
          value,
          total: Number(res?.data?.count) || rows.length,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBand(null);
        setCatalogue(null);
      });

    return () => {
      cancelled = true;
    };
    /* Re-counted when the catalogue itself changes - a medicine added, edited
       or made inactive - and not when the table is merely paged or filtered. */
  }, [refresh]);

  const columns = [
    {
      title: "Medicine",
      key: "name",
      render: (_, record) => (
        <StackedCell
          name={record.name}
          sub={[record.drug1_name, record.product_type, record.hsn_or_sac]
            .filter(Boolean)
            .join(" · ")}
        />
      ),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (value) => (value ? <Tag>{value}</Tag> : "-"),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      /*
       * Capped, and cut with an ellipsis past it.
       *
       * A category is a label, but nothing stops one being a paragraph - and
       * an uncapped column took the whole sentence, pushed the table far
       * wider than the screen and left Rate A and Status running underneath
       * the Action column that is pinned over them. The full text is in the
       * title for anyone who needs it.
       */
      width: 220,
      ellipsis: true,
      render: (value) =>
        value ? (
          <Tag>
            <span
              title={value}
              style={{
                display: "block",
                maxWidth: 190,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </span>
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Stock",
      key: "current_stock",
      align: "right",
      // The figure colours itself, so the row reads without hunting for Status.
      render: (_, record) => (
        <FigurePill tone={STATES[stateOf(record)].tone}>
          {Number(record.current_stock) || 0}
        </FigurePill>
      ),
    },
    /*
     * The two money columns are given a width, because their headings are
     * wider than their figures. Sized to content, "RATE A" broke across two
     * lines above a single-line column of numbers and left the heading
     * sitting at a different height from every other one on the row.
     */
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      align: "right",
      width: 104,
      render: (value) => <NumCell>{formatAmount(value, "-")}</NumCell>,
    },
    {
      title: "Rate A",
      dataIndex: "rate_a",
      key: "rate_a",
      align: "right",
      width: 104,
      render: (value) => <NumCell>{formatAmount(value, "-")}</NumCell>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const state = STATES[stateOf(record)];
        return <StatusPill tone={state.tone}>{state.label}</StatusPill>;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      /* Two 32px marks, the gap between them and the cell's own padding -
         nothing more. It was sized for four. */
      width: 76,
      render: (_, record) => (
        <RowActions>
          <IconAction
            icon="forms"
            label="View medicine"
            onClick={() => navigate(`/pharmacy/medicines/${record.id}`)}
          />
          <IconAction
            icon="edit"
            label="Edit medicine"
            onClick={() => {
              setEditData(record);
              setOpen(true);
            }}
          />
        </RowActions>
      ),
    },
  ];

  /*
   * Whatever the response carries is what the table shows. Nothing is narrowed
   * here - a chip the server does not filter on yet returns the whole list, and
   * that is visible rather than papered over.
   */
  /*
   * What the table shows.
   *
   * Active and Inactive are the server's to filter - it holds the flag. Low
   * Stock and Out of Stock are not: they are worked out from each medicine's
   * own stock against its own re-order level, and `?status=` is not
   * implemented on the endpoint, so asking for them came back unfiltered -
   * the heading said "Low Stock medicines" over a list of everything.
   *
   * They are answered here instead, out of the same catalogue the band was
   * counted from, and paged here too. A catalogue longer than that read is
   * the one case this cannot answer, and it says so rather than showing a
   * part of the truth as all of it.
   */
  const clientFiltered = Boolean(STOCK_STATUS_PARAM[state]) && catalogue;
  const matching = clientFiltered
    ? catalogue.filter((record) => stateOf(record) === state)
    : null;

  const rows = clientFiltered
    ? matching.slice((page - 1) * PHARMACY_PAGE_SIZE, page * PHARMACY_PAGE_SIZE)
    : data?.results || [];
  const total = clientFiltered ? matching.length : Number(data?.count) || 0;

  return (
    <ListScreen>
      <ListHead
        title="Medicines"
        actions={
          <>
            {canTransfer && (
              <UiButton
                variant="secondary"
                onClick={() => navigate("/pharmacy/stock-transfer")}
              >
                <CgIcon name="boxout" size={16} stroke={2} />
                Stock Transfer
              </UiButton>
            )}
            <UiButton
              onClick={() => {
                setEditData(null);
                setOpen(true);
              }}
            >
              <CgIcon name="plus" size={16} stroke={2.2} />
              Add Medicine
            </UiButton>
          </>
        }
      />

      {band && (
        <DistBand
          total={band.total}
          label="Medicines"
          segments={[
            { key: "active", label: "Active", count: band.counts.active, color: STATES.active.color },
            { key: "low", label: "Low Stock", count: band.counts.low, color: STATES.low.color },
            { key: "out", label: "Out of Stock", count: band.counts.out, color: STATES.out.color },
            { key: "inactive", label: "Inactive", count: band.counts.inactive, color: STATES.inactive.color },
          ]}
          activeKey={state}
          onSelect={(key) => {
            setPage(1);
            setState((prev) => (prev === key ? null : key));
          }}
          /*
           * What is on the shelf and what it is worth - counted over the whole
           * catalogue, which is the only way either figure is honest. They
           * used to be read off one page of it, which is why they were taken
           * off; the band reads the catalogue now, so they can come back.
           */
          meta={`${formatQty(band.units)} units on hand`}
          aside={{ label: "Stock Value", value: inr0(band.value) }}
        />
      )}

      <ListCard
        /* What is being looked at, named - a filtered table headed
           "Medicines" says nothing about which medicines. */
        title={state ? `${STATES[state].label} medicines` : "Medicines"}
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: "Search medicine, salt, company or HSN",
        }}
        onClear={
          search || state
            ? () => {
                setSearch("");
                setState(null);
                setPage(1);
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={rows}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>

      {open && (
        <MedicineFormModal
          open={open}
          setOpen={setOpen}
          editData={editData}
          refreshPage={refreshPage}
        />
      )}
    </ListScreen>
  );
};

export default Medicines;
